// src/actions/ingredients_actions.js
import axios from 'axios';
import {
  FETCH_INGREDIENTS_PAGE_INFO_LOADING,
  FETCH_INGREDIENTS_PAGE_INFO_SUCCESS,
  FETCH_INGREDIENTS_PAGE_INFO_FAIL,
  FETCH_RECIPE_INGREDIENTS_ANALYTICS_LOADING,
  FETCH_RECIPE_INGREDIENTS_ANALYTICS_SUCCESS,
  FETCH_RECIPE_INGREDIENTS_ANALYTICS_FAIL
} from './types';

/**
 * Fetch per-recipe ingredients analytics.
 * @param {string|number} recipeId - The recipe identifier.
 * @returns {Function} thunk
 */
export const fetchRecipeIngredientsAnalytics = (recipeId) => async (dispatch) => {
  if (!recipeId) {
    return dispatch({
      type: FETCH_RECIPE_INGREDIENTS_ANALYTICS_FAIL,
      payload: 'Missing recipeId'
    });
  }

  dispatch({ type: FETCH_RECIPE_INGREDIENTS_ANALYTICS_LOADING, meta: { recipeId } });

  try {
    const token = localStorage.getItem('jwtToken');
    const base = process.env.REACT_APP_ADMIN_API_ADDRESS || 'http://localhost:9890';
    const url = `${base}/secureapi/analytics/recipes/${recipeId}/ingredients`;

    const resp = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (resp?.data?.success === false) {
      return dispatch({
        type: FETCH_RECIPE_INGREDIENTS_ANALYTICS_FAIL,
        payload: resp.data?.message || 'Failed to fetch recipe ingredients analytics',
        meta: { recipeId }
      });
    }

    // Accept both wrapped ({success, data}) and raw array responses
    const payload = resp?.data?.data ?? resp?.data ?? [];
    dispatch({
      type: FETCH_RECIPE_INGREDIENTS_ANALYTICS_SUCCESS,
      payload,
      meta: { recipeId }
    });
  } catch (err) {
    dispatch({
      type: FETCH_RECIPE_INGREDIENTS_ANALYTICS_FAIL,
      payload: err.response?.data?.message || err.message || 'Failed to fetch recipe ingredients analytics',
      meta: { recipeId }
    });
  }
};

/**
 * Fetch top ingredients with counts and details.
 * Params:
 *  - interval: one of 'daily', 'weekly', 'monthly', 'yearly'
 *  - top: number of items to fetch
 *  - filter: stat type (0=view_list, 1=view, 2=make, 3=like, 4=shared)
 *  - table:  0 = Base/Liquor, 1 = Mixer
 */
export const fetchIngredientsPageInfo =
  ({ interval = 'weekly', top = '10', filter = '0', table = '0' }) =>
  async (dispatch) => {
    dispatch({ type: FETCH_INGREDIENTS_PAGE_INFO_LOADING });

    try {
      const token = localStorage.getItem('jwtToken');
      const base = process.env.REACT_APP_ADMIN_API_ADDRESS;
      // use the MERGED endpoint
      const url = `${base}/secureapi/analytics/ingredients/search-v2-merged`;

      const nowSec = Math.floor(Date.now() / 1000); // => MySQL-only

      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          interval,           // 'daily' | 'weekly' | 'monthly' | 'yearly'
          top,                // number as string is fine
          filter,             // 1=viewed, 2=crafted, etc.
          table,              // 0=base, 1=mixer
          // MYSQL-ONLY SWITCH:
          mode: 'sum',        // merge policy (kept for parity; PG will be 0 anyway)
          cutoverTs: nowSec   // PG counts > cutoverTs -> 0; MySQL counts <= cutoverTs
        }
      });

      if (resp.data?.success) {
        dispatch({
          type: FETCH_INGREDIENTS_PAGE_INFO_SUCCESS,
          payload: resp.data.data || []
        });
      } else {
        dispatch({
          type: FETCH_INGREDIENTS_PAGE_INFO_FAIL,
          payload: resp.data?.message || 'Failed to fetch ingredients analytics'
        });
      }
    } catch (err) {
      dispatch({
        type: FETCH_INGREDIENTS_PAGE_INFO_FAIL,
        payload: err.response?.data?.message || err.message || 'Failed to fetch ingredients analytics'
      });
    }
  };


